import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DefaultTextField from 'components/Utils/DefaultTextField';
import OHIPFields from 'components/Patient/OHIPFields';
import ChangePhone from 'components/Patient/ChangePhone';
import AddressFields from 'components/Patient/EditAddress/AddressFields';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import GenericTopBar from 'components/Utils/GenericTopBar';
import MenuItem from '@material-ui/core/MenuItem';
import './style.scss';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import { DefaultSwitch } from 'services/UIUtils';
import PatientBadge from 'components/Patient/PatientBadge';
import PatientSearchField from 'components/Patient/PatientSearchField';
import PharmacyBadge from 'components/Pharmacy/PharmacyBadge';
import { DefaultPharmacy } from 'components/HealthRecords/Prescriptions/NewPrescription/view';
import { useDefaultPharmacy } from 'hooks/useDefaultPharmacy';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { PERMISSIONS } from 'services/StaffUtils';
import PatientClearancesEditor from './PatientClearancesEditor';

const buttonStyle = { marginTop: '10px', width: '200px' };

const PatientInfoView = (props) => {
  const defaultPharmacy = useDefaultPharmacy(props.value);
  const [phoneNumberModal, setPhoneNumberModal] = useState(false);
  const permissions = useAppSelector((state) => state.authentication.permissions);
  const isSuperAdmin = permissions.includes(PERMISSIONS.super_admin);

  return (
    <div className="patient-info-body">
      <div className="content">
        <Section title="OHIP Information">
          <OHIPFields patient={props.patient} allowEdit={props.allowEdit} />
        </Section>

        <Section title="Patient Information">
          <div className="row">
            {!props.isPatient && (!props.value.health_card?.valid_card || isSuperAdmin) && (
              <PatientElements {...props} fields={personalDataFields} onChange={props.onChange} />
            )}
            <PatientElements {...props} fields={patientFields} onChange={props.onChange} />
            <DefaultSelectField
              key="clinic_id"
              name="clinic_id"
              label="Clinic"
              value={props.value.clinic_id}
              onChange={props.onChange}
              inputElements={[
                ...(props.clinics || []).map((clinic) => (
                  <MenuItem value={clinic.id} key={'select-' + clinic.id}>
                    {clinic.name}
                  </MenuItem>
                )),
              ]}
            />

            {phoneNumberModal && (
              <DefaultModal
                className="modal change-phone-modal"
                key="change-phone-modal"
                wrap={true}
                padding={true}
                title="Change Phone Number"
                content={
                  <ChangePhone
                    patient={props.patient}
                    onSuccess={() => setPhoneNumberModal(false)}
                  />
                }
                isOpen={phoneNumberModal}
                onRequestClose={() => setPhoneNumberModal(false)}
              />
            )}

            {props.isPatient && (
              <div style={{ marginTop: '23px' }}>
                <DefaultButton
                  name="Change Phone Number"
                  onClick={() => setPhoneNumberModal(true)}
                  style={{ width: '200px' }}
                />
              </div>
            )}

            {!props.isPatient && (
              <DefaultSwitch
                name="disable_home_appointments"
                onChange={(event, isInputChecked) =>
                  props.onChange({
                    target: {
                      value: isInputChecked,
                      name: 'disable_home_appointments',
                    },
                  })
                }
                label="Disable Home Appointments"
                padded={false}
                reversed={true}
                checked={!!props.value.disable_home_appointments}
              />
            )}
            {!props.isPatient && (
              <DefaultSwitch
                name="scribe_consent"
                onChange={(event, isInputChecked) =>
                  props.onChange({
                    target: {
                      value: isInputChecked,
                      name: 'scribe_consent',
                    },
                  })
                }
                label="AI Scribe Consent"
                padded={false}
                reversed={true}
                checked={!!props.value.scribe_consent}
              />
            )}
            <UsernameSection {...props} />
          </div>
        </Section>

        <Section title="Emergency Contact">
          <div className="row">
            <PatientElements
              {...props}
              fields={emergencyContactFields}
              onChange={props.onEmergencyContactChange}
            />
          </div>
        </Section>

        <Section title="Address">
          <div style={{ maxWidth: '500px', width: '100%' }}>
            <AddressFields
              showNotes={!props.isPatient}
              address={props.value.address}
              onChange={props.allowEdit ? props.onAddressChange : undefined}
              onSearchResult={props.allowEdit ? props.setAddress : undefined}
            />
          </div>
        </Section>

        {!props.isPatient && (
          <Section title="Patient Clearances">
            <PatientClearancesEditor
              clearances={props.value.clearances || []}
              onChange={(clearances) => props.setValue((prev) => ({ ...prev, clearances }))}
            />
          </Section>
        )}

        <DefaultButton
          id="save-button"
          name="Save Changes"
          style={buttonStyle}
          onClick={props.save}
          loading={props.submitting}
          disabled={!props.patientEdited}
          hide={!props.allowEdit}
        />

        <Section title="Default Pharmacy">
          {!props.isPatient && (
            <>
              <div className="row" style={{ alignItems: 'flex-start', gap: '10px' }}>
                <div style={{ width: '250px' }}>
                  <DefaultPharmacy
                    patient={props.patient}
                    setPharmacy={(pharmacy) => {
                      props.onChange({
                        target: { name: 'default_pharmacy_id', value: pharmacy?.id },
                      });
                    }}
                  />
                </div>
                <DefaultButton
                  name="Remove Pharmacy"
                  hide={!props.value.default_pharmacy_id}
                  onClick={() => {
                    props.onChange({
                      target: { name: 'default_pharmacy_id', value: undefined },
                    });
                  }}
                  className="red"
                  style={{
                    width: '160px',
                  }}
                />
              </div>
              <div className="row mt-4 full-width">
                {defaultPharmacy?.name !== undefined && (
                  <PharmacyBadge pharmacy={defaultPharmacy} />
                )}
              </div>
            </>
          )}
        </Section>

        {!props.isPatient && (
          <>
            <Section title="Email Confirmation">
              <DefaultTextField
                name="email_verified"
                label="Email Confirmed"
                value={props.patient.email_verified ? 'True' : 'False'}
                disabled={true}
                style={{ width: '250px', marginRight: '20px' }}
              />
              {!props.patient.email_verified && (
                <div>
                  <DefaultButton
                    name="Resend Email Confirmation"
                    className="blue"
                    style={{ width: '250px', marginTop: '30px' }}
                    onClick={props.resendEmailConfirmation}
                    loading={props.resendingEmailConfirmation}
                  />
                </div>
              )}
            </Section>
            <Section title="Account Status">
              <span
                style={{
                  display: 'block',
                  width: '100%',
                  height: '40px',
                }}
              >
                This account is currently {props.value.inactive ? 'inactive' : 'active'}
                {!props.value.inactive && props.value.login_disabled
                  ? ', but patient cannot login'
                  : ''}
                .
              </span>

              <div className="buttons flex gap-2">
                <DefaultButton
                  name={props.value.inactive ? 'Enable Account' : 'Disable Account'}
                  className={props.value.inactive ? 'blue' : 'red'}
                  onClick={props.toggleEnabled}
                  loading={props.submitting}
                />

                {!props.value.inactive && (
                  <DefaultButton
                    name={props.value.login_disabled ? 'Enable Login' : 'Disable Login'}
                    className={props.value.login_disabled ? 'blue' : 'red'}
                    onClick={props.toggleDisableLogin}
                    loading={props.submitting}
                  />
                )}
              </div>
            </Section>
            <Section title="Transfer Account Data">
              <TransferHRModal patient={props.value} />
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

const TransferHRModal = ({ patient }) => {
  const dispatch = useAppDispatch();
  const [showTransfer, setShowTransfer] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [to, setTo] = useState();
  const [transferring, setTransferring] = useState(false);

  function selectToPatient(toPatient) {
    if (!toPatient || toPatient.id !== patient.id) {
      setTo(toPatient);
    }
  }

  async function proceedTransfer() {
    setTransferring(true);
    const result = await dispatch(api.Patients.transferAccount({ from: patient.id, to: to.id }), {
      title: 'Transfer Account',
      successText: 'Account data transferred successfully!',
    });
    setTransferring(false);
    if (result) {
      setShowConfirmation(false);
      setShowTransfer(false);
    }
  }

  return (
    <React.Fragment>
      <DefaultButton
        name="Transfer"
        className="blue"
        onClick={() => setShowTransfer(!showTransfer)}
      />
      {showTransfer && (
        <DefaultModal
          className="modal transfer-health-records"
          wrap={true}
          padding={true}
          title="Transfer Health Records"
          content={
            <div>
              <span className="small-title">From:</span>
              <PatientBadge patient={patient} />
              <br />
              <PatientSearchField title="To:" onSelect={selectToPatient} patient={to} />
              <br />
              <div className="row">
                <DefaultButton
                  name="Transfer Account Data"
                  className="red"
                  style={{ margin: 'auto', width: '80%' }}
                  onClick={() => setShowConfirmation(true)}
                  loading={transferring}
                  disabled={!to}
                />
                {showConfirmation && (
                  <ConfirmationDialog
                    title="Confirmation Necessary"
                    open={showConfirmation}
                    message="CAUTION! This cannot be undone! Are you sure you want to transfer all the account information?"
                    onConfirm={proceedTransfer}
                    onCancel={() => setShowConfirmation(false)}
                    loadingState={transferring}
                  />
                )}
              </div>
            </div>
          }
          isOpen={showTransfer}
          onRequestClose={() => setShowTransfer(false)}
        />
      )}
    </React.Fragment>
  );
};

const UsernameSection = (props) =>
  props.onShowUsername !== undefined && (
    <div className="username">
      {props.usernameNotFound && (
        <DefaultButton name="Generate Credentials" onClick={props.onGenerateCredentials} />
      )}
      {!props.usernameNotFound &&
        (props.username === undefined ? (
          <DefaultButton name="Show Username" onClick={props.onShowUsername} />
        ) : (
          <DefaultTextField
            name="username"
            label="Username"
            disabled={true}
            value={props.username}
          />
        ))}
    </div>
  );

const Section = (props) => (
  <div className="row section">
    <GenericTopBar title={props.title} />
    {props.children}
  </div>
);

const PatientElements = (props) =>
  props.fields.map((field, index) => {
    return <PatientElement key={index} {...props} field={field} />;
  });

const PatientElement = ({ field, onChange, allowEdit, value, isPatient }) => {
  if (field.element !== undefined) return field.element(value, onChange, onChange);
  return (
    <DefaultTextField
      key={field.name}
      label={field.label}
      style={{ width: '200px' }}
      name={field.name}
      disabled={!allowEdit || (field.disabled !== undefined ? field.disabled(isPatient) : false)}
      onChange={onChange}
      value={field.value(value) || ''}
    />
  );
};

PatientInfoView.propTypes = {
  patient: PropTypes.object.isRequired,
  value: PropTypes.object,
  allowEdit: PropTypes.bool,
  patientEdited: PropTypes.bool,
  submitting: PropTypes.bool,
  sendMessage: PropTypes.func,
  onChange: PropTypes.func,
  onEmergencyContactChange: PropTypes.func,
  onAddressChange: PropTypes.func,
  setAddress: PropTypes.func,
  username: PropTypes.string,
  onShowUsername: PropTypes.func,
  save: PropTypes.func,
  clinics: PropTypes.array,
};

export default PatientInfoView;

const patientFields = [
  {
    label: 'Preferred Name',
    name: 'preferred_name',
    value: (patient) => patient.preferred_name,
  },
  {
    label: 'Preferred Pronoun',
    name: 'preferred_pronoun',
    value: (patient) => patient.preferred_pronoun,
  },
  {
    label: 'Phone Number',
    name: 'phone',
    disabled: (isPatient) => isPatient,
    value: (patient) => {
      return patient.phone;
    },
  },
  {
    label: 'Phone Number 2',
    name: 'phone2',
    value: (patient) => {
      return patient.phone2;
    },
  },
  {
    label: 'Email',
    name: 'email',
    value: (patient) => {
      return patient.email;
    },
  },
];

const personalDataFields = [
  {
    label: 'First Name',
    name: 'first_name',
    value: (patient) => {
      return patient.first_name;
    },
  },
  {
    label: 'Second Name',
    name: 'second_name',
    value: (patient) => {
      return patient.second_name;
    },
  },
  {
    label: 'Last Name',
    name: 'last_name',
    value: (patient) => {
      return patient.last_name;
    },
  },
  {
    element: (patient, onChange) => (
      <DefaultSelectField
        label="Gender"
        name="gender"
        value={patient.gender}
        onChange={onChange}
        inputs={['Male', 'Female']}
      />
    ),
  },
  {
    element: (patient, onChange) => (
      <DefaultDatePicker
        key="dateOfBirth"
        name="date_of_birth"
        label="Date of Birth"
        style={{ width: '250px' }}
        onChange={(event, value) => {
          let change = { target: { name: 'date_of_birth', value } };
          onChange(change);
        }}
        value={patient.date_of_birth}
      />
    ),
  },
];

const emergencyContactFields = [
  {
    label: 'Name',
    name: 'name',
    value: (patient) => {
      let emergency = patient.emergency_contact || {};
      return emergency.name;
    },
  },
  {
    label: 'Contact',
    name: 'contact',
    value: (patient) => {
      let emergency = patient.emergency_contact || {};
      return emergency.contact;
    },
  },
  {
    label: 'Relation',
    name: 'relation',
    value: (patient) => {
      let emergency = patient.emergency_contact || {};
      return emergency.relation;
    },
  },
];
